import type jaCatalog from "./ja.json";

type CatalogValue = string | string[];
type Catalog = Record<string, CatalogValue>;
type I18nPrimitiveValue = string | number | boolean | null | undefined;
type I18nCaseValue = Record<string, I18nPrimitiveValue>;
type I18nValue = I18nPrimitiveValue | I18nCaseValue;
type I18nValues = Record<string, I18nValue>;
type CatalogGlob = (pattern: string) => Record<string, () => Promise<{ default: Catalog }>>;

const DEFAULT_LOCALE = "ja";
const catalogLoaders = (import.meta as ImportMeta & { glob: CatalogGlob }).glob("./*.json");
const catalogs: Partial<Record<GameLocale, Catalog>> = {};
const localeCodes = Object.keys(catalogLoaders)
  .map((path) => /^\.\/(.+)\.json$/.exec(path)?.[1])
  .filter((locale): locale is GameLocale => Boolean(locale))
  .sort();
const localeAliases: Record<string, GameLocale> = {
  zh: "zh-hans",
  "zh-cn": "zh-hans",
  "zh-sg": "zh-hans",
  "zh-my": "zh-hans",
  "zh-tw": "zh-hant",
  "zh-hk": "zh-hant",
  "zh-mo": "zh-hant"
};
let currentLocale: GameLocale = DEFAULT_LOCALE;

export type I18nTextId = keyof typeof jaCatalog;
export type GameLocale = string;

export function gameText(id: I18nTextId | string, values: I18nValues = {}): string {
  const catalog = catalogs[currentLocale];
  const defaultCatalog = currentLocale === DEFAULT_LOCALE ? catalog : catalogs[DEFAULT_LOCALE];
  return formatTemplate(catalogValueText(catalog?.[id] ?? defaultCatalog?.[id], id), values, currentLocale);
}

export function gameLocale(): GameLocale {
  return currentLocale;
}

export function setGameLocale(locale: string | null | undefined): GameLocale {
  currentLocale = locale === undefined || locale === null ? browserPreferredGameLocale() : normalizeGameLocale(locale);
  return currentLocale;
}

export async function loadGameLocale(locale: string | null | undefined): Promise<GameLocale> {
  const nextLocale = setGameLocale(locale);
  await loadCatalog(nextLocale);
  return nextLocale;
}

export function normalizeGameLocale(locale: string | null | undefined): GameLocale {
  return supportedLocaleForTag(locale) ?? DEFAULT_LOCALE;
}

export function browserPreferredGameLocale(): GameLocale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of languages) {
    const supported = supportedLocaleForTag(language);
    if (supported) return supported;
  }
  return DEFAULT_LOCALE;
}

function formatTemplate(template: string, values: I18nValues, locale: GameLocale): string {
  let output = "";
  for (let index = 0; index < template.length; index += 1) {
    const character = template[index];
    if (character !== "{") {
      output += character;
      continue;
    }
    const expression = readBracedExpression(template, index);
    if (!expression) {
      output += character;
      continue;
    }
    output += formatExpression(expression.content, values, locale) ?? `{${expression.content}}`;
    index = expression.endIndex;
  }
  return output;
}

function catalogValueText(value: CatalogValue | undefined, fallback: string): string {
  if (Array.isArray(value)) return value.join("\n");
  return value ?? fallback;
}

async function loadCatalog(locale: GameLocale): Promise<Catalog> {
  if (catalogs[locale]) return catalogs[locale];
  const loader = catalogLoaders[`./${locale}.json`];
  if (!loader) {
    if (locale === DEFAULT_LOCALE) throw new Error(`Missing default i18n catalog: ${DEFAULT_LOCALE}`);
    return loadCatalog(DEFAULT_LOCALE);
  }
  const module = await loader();
  catalogs[locale] = module.default;
  return module.default;
}

function readBracedExpression(template: string, startIndex: number): { content: string; endIndex: number } | undefined {
  let depth = 0;
  for (let index = startIndex; index < template.length; index += 1) {
    const character = template[index];
    if (character === "{") depth += 1;
    if (character === "}") {
      depth -= 1;
      if (depth === 0) {
        return { content: template.slice(startIndex + 1, index), endIndex: index };
      }
    }
  }
  return undefined;
}

function formatExpression(expression: string, values: I18nValues, locale: GameLocale): string | undefined {
  const parts = splitTopLevelCommas(expression, 3);
  if (parts.length >= 3) {
    const [rawKey, rawType, rawOptions] = parts;
    const key = rawKey.trim();
    const type = rawType.trim();
    if (type === "plural" || type === "selectordinal") {
      return formatPluralExpression(key, type, rawOptions, values, locale);
    }
    if (type === "select") {
      return formatSelectExpression(key, rawOptions, values, locale);
    }
  }

  const match = /^([A-Za-z0-9_]+)(?::([A-Za-z0-9_-]+))?$/.exec(expression.trim());
  if (!match) return undefined;
  return formatValue(match[1], match[2], values);
}

function splitTopLevelCommas(text: string, maxParts: number): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === "{") depth += 1;
    else if (character === "}") depth = Math.max(0, depth - 1);
    else if (character === "," && depth === 0 && parts.length + 1 < maxParts) {
      parts.push(text.slice(start, index));
      start = index + 1;
    }
  }
  parts.push(text.slice(start));
  return parts;
}

function formatPluralExpression(
  key: string,
  type: "plural" | "selectordinal",
  options: string,
  values: I18nValues,
  locale: GameLocale
): string | undefined {
  const value = numericValue(values[key]);
  if (value === undefined) return undefined;
  const variants = parseVariantOptions(options);
  const exactKey = `=${value}`;
  const category = new Intl.PluralRules(locale, { type: type === "selectordinal" ? "ordinal" : "cardinal" }).select(value);
  const selected = variants.get(exactKey) ?? variants.get(category) ?? variants.get("other");
  if (selected === undefined) return undefined;
  return formatTemplate(selected.replace(/#/g, String(value)), values, locale);
}

function formatSelectExpression(key: string, options: string, values: I18nValues, locale: GameLocale): string | undefined {
  const value = stringValue(values[key]);
  if (value === undefined) return undefined;
  const variants = parseVariantOptions(options);
  const selected = variants.get(value) ?? variants.get("other");
  return selected === undefined ? undefined : formatTemplate(selected, values, locale);
}

function parseVariantOptions(options: string): Map<string, string> {
  const variants = new Map<string, string>();
  let index = 0;
  while (index < options.length) {
    while (/\s/.test(options[index] ?? "")) index += 1;
    const keyStart = index;
    while (index < options.length && !/\s|\{/.test(options[index])) index += 1;
    const key = options.slice(keyStart, index);
    while (/\s/.test(options[index] ?? "")) index += 1;
    if (!key || options[index] !== "{") break;
    const expression = readBracedExpression(options, index);
    if (!expression) break;
    variants.set(key, expression.content);
    index = expression.endIndex + 1;
  }
  return variants;
}

function formatValue(key: string, caseName: string | undefined, values: I18nValues): string | undefined {
  const value = values[key];
  if (isCaseValue(value)) {
    const selected = (caseName ? value[caseName] : undefined) ?? value.value ?? value.other ?? value.nominative;
    return selected === undefined || selected === null ? undefined : String(selected);
  }
  return value === undefined || value === null ? undefined : String(value);
}

function numericValue(value: I18nValue): number | undefined {
  const rawValue = isCaseValue(value) ? value.value ?? value.other ?? value.nominative : value;
  if (typeof rawValue === "number") return Number.isFinite(rawValue) ? rawValue : undefined;
  if (typeof rawValue === "string" && rawValue.trim() !== "") {
    const numberValue = Number(rawValue);
    return Number.isFinite(numberValue) ? numberValue : undefined;
  }
  return undefined;
}

function stringValue(value: I18nValue): string | undefined {
  const rawValue = isCaseValue(value) ? value.value ?? value.other ?? value.nominative : value;
  return rawValue === undefined || rawValue === null ? undefined : String(rawValue);
}

function isCaseValue(value: I18nValue): value is I18nCaseValue {
  return typeof value === "object" && value !== null;
}

function supportedLocaleForTag(locale: string | null | undefined): GameLocale | undefined {
  if (!locale) return undefined;
  const normalized = locale.toLowerCase();
  const directMatch = localeCodes.find((code) => normalized === code || normalized.startsWith(`${code}-`));
  if (directMatch) return directMatch;
  const alias = localeAliases[normalized] ?? localeAliases[normalized.split("-")[0] ?? ""];
  return alias && localeCodes.includes(alias) ? alias : undefined;
}
